import { useState, useEffect, useContext, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Grid3X3, List, Loader2, Map } from "lucide-react";
import axios from "axios";
import { AppContext, API } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import SEO, { SEO_DATA } from "@/components/SEO";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Lazy load map component for better performance
const PropertyMap = lazy(() => import("@/components/PropertyMap"));

const PropertiesPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { currency, formatPrice, formatArea } = useContext(AppContext);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid"); // grid, list, map
  const [sortBy, setSortBy] = useState("newest");
  const [selectedProperty, setSelectedProperty] = useState(null);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams(searchParams);
        const response = await axios.get(`${API}/properties?${params.toString()}`);
        let data = response.data;

        // Sort properties
        if (sortBy === "price_asc") {
          data.sort((a, b) => a.price_usd - b.price_usd);
        } else if (sortBy === "price_desc") {
          data.sort((a, b) => b.price_usd - a.price_usd);
        } else if (sortBy === "area_desc") {
          data.sort((a, b) => b.area_m2 - a.area_m2);
        }
        // newest is default from API

        setProperties(data);
      } catch (error) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [searchParams, sortBy]);

  const handleSearch = async (filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          if ((key === "min_price" || key === "max_price") && currency === "GTQ") {
            params.append(key, Math.round(parseFloat(value) / 7.80));
          } else {
            params.append(key, value);
          }
        }
      });

      const response = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const getActiveFilters = () => {
    const filters = [];
    if (searchParams.get("transaction_type")) {
      filters.push(searchParams.get("transaction_type") === "Venta" ? "Compra" : "Alquiler");
    }
    if (searchParams.get("property_type")) {
      filters.push(searchParams.get("property_type"));
    }
    if (searchParams.get("department")) {
      filters.push(searchParams.get("department"));
    }
    return filters;
  };

  const activeFilters = getActiveFilters();

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="properties-page">
      <SEO 
        title={SEO_DATA.properties.title}
        description={SEO_DATA.properties.description}
        keywords={SEO_DATA.properties.keywords}
        url="/propiedades"
      />
      <Header />

      {/* Page Header */}
      <section className="pt-32 pb-8 md:pt-40 md:pb-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
              {t('properties.subtitle')}
            </span>
            <h1 className="mt-3 text-3xl md:text-4xl lg:text-5xl text-white">
              {t('properties.title')}
            </h1>
            {activeFilters.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {activeFilters.map((filter) => (
                  <span 
                    key={filter}
                    className="px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-sm rounded-sm"
                  >
                    {filter}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="pb-8">
        <div className="container-custom">
          <SearchBar onSearch={handleSearch} compact />
        </div>
      </section>

      {/* Results */}
      <section className="pb-24">
        <div className="container-custom">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-6 border-b border-white/5">
            <p className="text-[#A3A3A3]">
              {loading ? (
                t('common.loading')
              ) : (
                <>
                  <span className="text-white font-medium">{properties.length}</span>
                  {" "}{t('properties.found', { count: properties.length })}
                </>
              )}
            </p>

            <div className="flex items-center gap-4">
              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger 
                  className="w-44 bg-transparent border-white/10 text-white"
                  data-testid="sort-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="newest">{t('properties.sort.newest')}</SelectItem>
                  <SelectItem value="price_asc">{t('properties.sort.priceAsc')}</SelectItem>
                  <SelectItem value="price_desc">{t('properties.sort.priceDesc')}</SelectItem>
                  <SelectItem value="area_desc">{t('properties.sort.areaDesc')}</SelectItem>
                </SelectContent>
              </Select>

              {/* View Toggle */}
              <div className="hidden sm:flex border border-white/10 rounded-sm">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 ${viewMode === "grid" ? "bg-[#D4AF37] text-black" : "text-white/60 hover:text-white"}`}
                  data-testid="view-grid-btn"
                  title="Vista en cuadrícula"
                >
                  <Grid3X3 size={18} />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 ${viewMode === "list" ? "bg-[#D4AF37] text-black" : "text-white/60 hover:text-white"}`}
                  data-testid="view-list-btn"
                  title="Vista en lista"
                >
                  <List size={18} />
                </button>
                <button
                  onClick={() => setViewMode("map")}
                  className={`p-2 ${viewMode === "map" ? "bg-[#D4AF37] text-black" : "text-white/60 hover:text-white"}`}
                  data-testid="view-map-btn"
                  title="Vista en mapa"
                >
                  <Map size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Properties Grid/List/Map */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-white mb-4">No se encontraron propiedades</p>
              <p className="text-[#A3A3A3] mb-8">{t('properties.noResultsHint')}</p>
              <Button
                variant="outline"
                onClick={() => window.location.href = "/propiedades"}
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
              >
                {t('properties.viewAll')}
              </Button>
            </div>
          ) : viewMode === "map" ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Map */}
              <div className="order-1 lg:order-2">
                <Suspense fallback={
                  <div className="h-[500px] lg:h-[calc(100vh-300px)] bg-[#121212] rounded-lg flex items-center justify-center">
                    <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
                  </div>
                }>
                  <PropertyMap 
                    properties={properties}
                    selectedProperty={selectedProperty}
                    onPropertySelect={setSelectedProperty}
                    formatPrice={formatPrice}
                    formatArea={formatArea}
                    height="calc(100vh - 300px)"
                    className="sticky top-24"
                  />
                </Suspense>
              </div>
              
              {/* Property List beside map */}
              <div className="order-2 lg:order-1 space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                {properties.map((property, index) => (
                  <div 
                    key={property.id}
                    onClick={() => setSelectedProperty(property)}
                    className={`cursor-pointer transition-all ${
                      selectedProperty?.id === property.id 
                        ? "ring-2 ring-[#D4AF37] rounded-lg" 
                        : ""
                    }`}
                  >
                    <PropertyCard 
                      property={property} 
                      index={index}
                      compact
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={viewMode === "grid" ? "property-grid" : "space-y-6"}>
              {properties.map((property, index) => (
                <PropertyCard 
                  key={property.id} 
                  property={property} 
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertiesPage;
